import { EChartsOption } from 'echarts';
import { VoltageSpeedDashboardSample } from '../../voltage-with-speed.models';

interface TimelinePoint {
  second: number;
  speed: number | null;
  voltage: number | null;
  engine: number;
}

const digits = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, useGrouping: false });

function clock(second: number): string {
  const time = Math.max(0, Math.min(86400, Math.round(second)));
  return [Math.floor(time / 3600), Math.floor(time / 60) % 60, time % 60]
    .map(value => String(value).padStart(2, '0')).join(':');
}

export function getTimelinePoints(samples: VoltageSpeedDashboardSample[]): TimelinePoint[] {
  return (samples || []).flatMap(sample => {
    const time = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(sample.strTime?.trim());
    if (!time) return [];
    const [, hour, minute, second = '0'] = time;
    if (+hour > 23 || +minute > 59 || +second > 59) return [];
    const speed = sample.ValidSpeed === undefined ? sample.iSpeed : sample.ValidSpeed;
    const voltage = sample.ValidVoltage === undefined ? sample.fPower : sample.ValidVoltage;
    return [{
      second: +hour * 3600 + +minute * 60 + +second,
      speed: typeof speed === 'number' && Number.isFinite(speed) && speed >= 0 ? speed : null,
      voltage: typeof voltage === 'number' && Number.isFinite(voltage) && voltage >= 0 ? voltage : null,
      engine: sample.bOnOff
    }];
  }).sort((a, b) => a.second - b.second);
}

export function buildTimelineOptions(points: TimelinePoint[]): EChartsOption {
  return {
    animation: false,
    textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif', color: '#91a6bc' },
    color: ['#43a6ff', '#ffb45e'],
    aria: { enabled: true, description: 'نمودار سرعت و ولتاژ ثبت‌شده در طول روز؛ سرعت روی محور چپ و ولتاژ روی محور راست.' },
    grid: { left: 42, right: 42, top: 18, bottom: 65 },
    legend: { show: false, data: ['سرعت', 'ولتاژ'], selected: { سرعت: true, ولتاژ: true } },
    tooltip: {
      trigger: 'axis', confine: true, renderMode: 'html',
      backgroundColor: '#06111ff2', borderColor: '#41627d', borderWidth: 1, padding: 11,
      extraCssText: 'min-width:170px;border-radius:10px;line-height:2;box-shadow:0 10px 20px #0008;direction:rtl;text-align:right;pointer-events:none;',
      textStyle: { color: '#edf5ff', fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif', fontSize: 13 },
      axisPointer: { type: 'line', lineStyle: { color: '#d2e6f5', type: [3, 5] } },
      formatter: params => {
        const items = Array.isArray(params) ? params : [params];
        const point = points[items[0]?.dataIndex];
        if (!point) return '';
        const speed = point.speed == null ? 'نامعتبر' : `<span dir="ltr">${digits.format(point.speed)} km/h</span>`;
        const voltage = point.voltage == null ? 'نامعتبر' : `<span dir="ltr">${point.voltage.toFixed(2)} V</span>`;
        const engine = point.engine === 1 ? (point.speed == null ? 'موتور روشن' : point.speed > 0 ? 'در حال حرکت' : 'موتور روشن، توقف')
          : point.engine === 0 ? 'موتور خاموش' : 'وضعیت موتور نامشخص';
        return `<b dir="ltr">${clock(point.second)}</b><br>سرعت: ${speed}<br>ولتاژ: ${voltage}<br>${engine}`;
      }
    },
    xAxis: {
      type: 'value', min: 0, max: 86400, interval: 10800,
      axisLabel: { color: '#93abc0', fontSize: 11, hideOverlap: true, formatter: (value: number) => clock(value).slice(0, 5) },
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }
    },
    yAxis: [
      {
        type: 'value', name: 'km/h', position: 'left', min: 0, splitNumber: 5,
        max: extent => Math.ceil(extent.max / 20) * 20 || 20,
        nameGap: 7, nameTextStyle: { color: '#a3b9cc', fontSize: 10, align: 'left' },
        axisLabel: { color: '#93abc0', fontSize: 11, formatter: (value: number) => digits.format(value) },
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { lineStyle: { color: '#294058', type: [4, 5], width: 1 } }
      },
      {
        type: 'value', name: 'V', position: 'right', scale: true, splitNumber: 5,
        min: extent => extent.min - Math.max(0.18, (extent.max - extent.min) * 0.1),
        max: extent => extent.max + Math.max(0.18, (extent.max - extent.min) * 0.1),
        nameGap: 7, nameTextStyle: { color: '#a3b9cc', fontSize: 10, align: 'right' },
        axisLabel: { color: '#93abc0', fontSize: 11, formatter: (value: number) => value.toFixed(1) },
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { show: false }
      }
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, filterMode: 'none', start: 0, end: 100 },
      {
        type: 'slider', xAxisIndex: 0, filterMode: 'none', start: 0, end: 100,
        left: 42, right: 42, bottom: 4, height: 20, showDetail: false,
        borderColor: '#263a50', backgroundColor: '#15283c', fillerColor: '#43a6ff22',
        handleStyle: { color: '#91a6bc', borderColor: '#41627d' },
        moveHandleStyle: { color: '#41627d' },
        dataBackground: { lineStyle: { color: '#91a6bc' }, areaStyle: { color: '#263a50' } },
        selectedDataBackground: { lineStyle: { color: '#43a6ff' }, areaStyle: { color: '#204b72' } }
      }
    ],
    series: [
      {
        name: 'سرعت', type: 'line', yAxisIndex: 0, showSymbol: points.length === 1,
        connectNulls: false, lineStyle: { width: 2.5, cap: 'round', join: 'round' },
        emphasis: { disabled: true },
        data: points.map(point => [point.second, point.speed ?? '-'])
      },
      {
        name: 'ولتاژ', type: 'line', yAxisIndex: 1, showSymbol: points.length === 1,
        connectNulls: false, lineStyle: { width: 2.5, cap: 'round', join: 'round' },
        emphasis: { disabled: true },
        data: points.map(point => [point.second, point.voltage ?? '-'])
      }
    ]
  };
}
